import { useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { ThreeWayRebaseResult } from "@/lib/editor/three-way-rebase";
import { AlertTriangle, Check, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";

interface ConflictResolutionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conflictResult: ThreeWayRebaseResult | null;
  onKeepLocal: () => void;
  onUseServer: () => void;
  isResolving?: boolean;
}

export function ConflictResolutionModal({
  open,
  onOpenChange,
  conflictResult,
  onKeepLocal,
  onUseServer,
  isResolving = false,
}: ConflictResolutionModalProps) {
  const [showDetails, setShowDetails] = useState(false);

  if (!open || !conflictResult) return null;

  const profileCollisions = conflictResult.collidingProfileFields;
  const sectionCollisions = conflictResult.collidingSections;
  const totalCollisions = profileCollisions.length + sectionCollisions.length;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Draft Version Conflict Detected"
      description="A newer version of your profile was saved from another browser tab or device."
      className="max-w-xl"
    >
      <div className="space-y-4 pt-1">
        <div className="rounded-lg bg-amber-50 dark:bg-amber-950/40 p-3.5 border border-amber-200 dark:border-amber-800/60 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2.5">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="leading-relaxed">
            <span className="font-semibold">{totalCollisions} concurrent collision{totalCollisions === 1 ? "" : "s"} detected.</span>{" "}
            Choose how you would like to resolve the differences to prevent overwriting your work.
          </div>
        </div>

        {/* ── Collapsible Differences Review ── */}
        <div className="border rounded-lg overflow-hidden text-xs">
          <button
            type="button"
            onClick={() => setShowDetails(!showDetails)}
            className="w-full flex items-center justify-between p-3 bg-muted/40 hover:bg-muted/70 font-medium transition-colors cursor-pointer"
          >
            <span>Review Colliding Items ({totalCollisions})</span>
            {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {showDetails && (
            <div className="p-3 space-y-3 max-h-60 overflow-y-auto divide-y">
              {profileCollisions.map((p) => (
                <div key={p.field} className="pt-2 first:pt-0 space-y-1">
                  <div className="font-semibold text-foreground flex items-center gap-1.5">
                    <Badge variant="outline" className="text-[10px] uppercase font-mono">Profile Field</Badge>
                    <span>{p.field}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <div className="bg-muted/50 p-2 rounded border">
                      <div className="text-muted-foreground font-medium mb-0.5">Your Local Edit:</div>
                      <div className="truncate font-mono">{String(p.localValue || "(empty)")}</div>
                    </div>
                    <div className="bg-muted/50 p-2 rounded border">
                      <div className="text-muted-foreground font-medium mb-0.5">Server Version:</div>
                      <div className="truncate font-mono">{String(p.serverValue || "(empty)")}</div>
                    </div>
                  </div>
                </div>
              ))}

              {sectionCollisions.map((s) => (
                <div key={s.sectionId} className="pt-2 first:pt-0 space-y-1">
                  <div className="font-semibold text-foreground flex items-center gap-1.5">
                    <Badge variant="outline" className="text-[10px] uppercase font-mono">Section ({s.sectionType})</Badge>
                    <span className="truncate">{s.sectionId}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Collision kind: <span className="font-mono text-foreground">{s.kind}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Action Buttons ── */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={onUseServer}
            disabled={isResolving}
            className="h-9 text-xs gap-1.5"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            <span>Use Server Version</span>
          </Button>

          <Button
            onClick={onKeepLocal}
            disabled={isResolving}
            className="h-9 text-xs bg-primary text-primary-foreground hover:bg-primary/90 font-medium gap-1.5 shadow-xs"
          >
            <Check className="h-3.5 w-3.5" />
            <span>Keep My Changes &amp; Overwrite</span>
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
